'use client';

import { useEffect, useRef } from 'react';
import { nextTick } from 'utils/next-tick';

/**
 * useOnClickOutside
 *
 * Determine when the user clicks outside of a target element.
 *
 * @example
 *
 *
 * function onClickOutside(e) {
 *   console.log('The user clicked outside')
 * }
 *
 * const { bind } = useOnClickOutside(onClickOutside, {
 *   enabled: true
 * })
 *
 * <Button {...bind}>Button</Button>
 */

type Handler = (e: PointerEvent) => void;

export type UseOnClickOutsideOptions = {
  enabled?: boolean;
};

export function useOnClickOutside(
  handler: Handler,
  { enabled = true }: UseOnClickOutsideOptions = {}
) {
  const onClickInsideRef = useRef(false);
  const handlerRef = useRef(handler);

  // Since we only want the `useEffect` to run when enabled
  // actually changes, we update and access our event
  // handler in a ref.
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    if (!enabled) {
      onClickInsideRef.current = false;
      return;
    }

    let timer: any;
    function onClick(e: PointerEvent) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!onClickInsideRef.current) handlerRef.current?.(e);
        onClickInsideRef.current = false;
      });
    }

    nextTick(() => {
      document.addEventListener('pointerdown', onClick, { capture: true });
    });

    // eslint-disable-next-line consistent-return
    return () => {
      nextTick(() => {
        document.removeEventListener('pointerdown', onClick, { capture: true });
      });
      clearTimeout(timer);
    };
  }, [enabled]);

  return {
    bind: {
      onPointerDownCapture: enabled
        ? () => {
            onClickInsideRef.current = true;
          }
        : undefined,
    },
  };
}
